import { User, Mail, Calendar, MapPin } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Textarea } from '@/components/ui/Textarea';
import { PhoneDisplay } from '@/components/ui/PhoneDisplay';
import { CALENDAR_CATEGORIES, type CalendarCategory } from '@/lib/constants';
import { useState } from 'react';

interface ConfirmStepProps {
  contact: {
    firstName: string;
    lastName: string;
    phone: string;
    email: string;
  };
  category: CalendarCategory;
  locationName?: string;
  onConfirm: (notes: string) => void;
  isLoading: boolean;
}

export function ConfirmStep({
  contact,
  category,
  locationName,
  onConfirm,
  isLoading,
}: ConfirmStepProps) {
  const [notes, setNotes] = useState('');
  const categoryInfo = CALENDAR_CATEGORIES[category];

  return (
    <div>
      {/* Summary */}
      <div className="bg-slate-700/30 rounded-lg p-4 mb-5">
        <h3 className="text-sm text-slate-400 mb-3">Zusammenfassung</h3>

        {/* Contact info */}
        <div className="flex items-start gap-3 mb-4">
          <div className="w-10 h-10 bg-slate-600 rounded-full flex items-center justify-center shrink-0">
            <User className="w-5 h-5 text-slate-300" />
          </div>
          <div>
            <p className="font-medium text-slate-100">
              {contact.firstName} {contact.lastName}
            </p>
            <PhoneDisplay
              value={contact.phone}
              showIcon
              iconClassName="w-3 h-3"
              className="text-sm text-slate-400"
            />
            {contact.email && (
              <p className="text-sm text-slate-400 flex items-center gap-1">
                <Mail className="w-3 h-3" />
                {contact.email}
              </p>
            )}
          </div>
        </div>

        {/* Appointment type */}
        <div className="flex items-center gap-3 mb-3">
          <div
            className="w-10 h-10 rounded-full flex items-center justify-center shrink-0"
            style={{ backgroundColor: categoryInfo.color + '20' }}
          >
            <Calendar className="w-5 h-5" style={{ color: categoryInfo.color }} />
          </div>
          <div>
            <p className="font-medium text-slate-100">{categoryInfo.label}</p>
            <p className="text-sm text-slate-400">Sofort-Termin</p>
          </div>
        </div>

        {/* Location */}
        {locationName && (
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-slate-600 rounded-full flex items-center justify-center shrink-0">
              <MapPin className="w-5 h-5 text-slate-300" />
            </div>
            <div>
              <p className="text-sm text-slate-400">Standort</p>
              <p className="font-medium text-slate-100">{locationName}</p>
            </div>
          </div>
        )}
      </div>

      {/* Notes */}
      <Textarea
        label="Notizen (optional)"
        placeholder="Anliegen des Kunden, besondere Wünsche..."
        value={notes}
        onChange={(e) => setNotes(e.target.value)}
        rows={3}
        className="mb-6"
      />

      {/* Confirm button */}
      <Button
        onClick={() => onConfirm(notes)}
        isLoading={isLoading}
        className="w-full"
      >
        Kontakt & Termin erstellen
      </Button>
    </div>
  );
}
